import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  LayoutDashboard, Users, Package, Percent, Settings, FileText,
  GitPullRequest, CheckSquare, Wallet, Mail, BarChart3,
  Menu, X, ChevronDown, UserCog, Calculator, Landmark, Grid3X3
} from 'lucide-react';

// RBAC: Sidebar navigation - must match route-level RBAC in App.js
// accounts_team can ONLY access: Dashboard, Payouts, Reports
// relationship_manager can ONLY access: Dashboard, Leads, Partners, Payout Rate Card
const allLinks = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true, roles: ['super_admin', 'admin', 'relationship_manager', 'accounts_team', 'finance', 'compliance'] },
  { to: '/admin/leads', icon: GitPullRequest, label: 'Lead Pipeline', end: false, roles: ['super_admin', 'admin', 'relationship_manager'] },
  { to: '/admin/partners', icon: Users, label: 'Partners', end: false, roles: ['super_admin', 'admin', 'relationship_manager'] },
  { to: '/admin/banks', icon: Landmark, label: 'Banks', end: false, roles: ['super_admin', 'admin'] },
  { to: '/admin/loan-products', icon: Package, label: 'Loan Products', end: false, roles: ['super_admin', 'admin'] },
  { to: '/admin/loan-types', icon: Package, label: 'Loan Types', end: false, roles: ['super_admin', 'admin'] },
  { to: '/admin/matrix', icon: Grid3X3, label: 'Master Matrix', end: false, roles: ['super_admin', 'admin'] },
  { to: '/admin/commissions', icon: Percent, label: 'Commission Schemes', end: false, roles: ['super_admin', 'admin'] },
  { to: '/admin/commission-approvals', icon: CheckSquare, label: 'Commission Approvals', end: false, roles: ['super_admin', 'admin', 'finance'] },
  { to: '/admin/commission-card', icon: Calculator, label: 'Payout Rate Card', end: false, roles: ['super_admin', 'admin', 'finance', 'relationship_manager'] },
  { to: '/admin/payouts', icon: Wallet, label: 'Partner Payouts Mgmt.', end: false, roles: ['super_admin', 'admin', 'accounts_team', 'finance'] },
  { to: '/admin/email-templates', icon: Mail, label: 'Email Templates', end: false, roles: ['super_admin', 'admin'] },
  { to: '/admin/reports', icon: BarChart3, label: 'Reports & Exports', end: false, roles: ['super_admin', 'admin', 'accounts_team', 'finance'] },
  { to: '/admin/audit-logs', icon: FileText, label: 'Audit Logs', end: false, roles: ['super_admin', 'admin', 'finance', 'compliance'] },
  { to: '/admin/users', icon: UserCog, label: 'User Management', end: false, roles: ['super_admin', 'admin'] },
  { to: '/admin/settings', icon: Settings, label: 'Settings', end: false, roles: ['super_admin', 'admin'] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [profileOpen, setProfileOpen] = React.useState(false);

  // Lock body scroll when mobile sidebar is open
  React.useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  const userRoles = user?.roles || [];
  const visibleLinks = allLinks.filter(link => link.roles.some(r => userRoles.includes(r)));

  const getRoleLabel = () => {
    if (userRoles.includes('accounts_team')) return 'Accounts Portal';
    if (userRoles.includes('relationship_manager')) return 'RM Portal';
    if (userRoles.includes('finance')) return 'Finance Portal';
    return 'Admin Portal';
  };

  const userName = user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : 'Admin';

  return (
    <div className="flex h-screen w-full overflow-hidden" data-testid="admin-layout">
      {/* SIDEBAR */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-[230px] bg-[#0B1A33] flex flex-col shrink-0 transform transition-transform lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="px-5 pt-6 pb-8">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="DhanSthal" className="h-9 w-auto object-contain" />
          </div>
          <button className="lg:hidden absolute top-5 right-4 text-[#6b8ab5] hover:text-white" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 flex flex-col gap-[2px] overflow-y-auto">
          {visibleLinks.map(link => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-[10px] rounded-lg text-[13px] font-medium transition-colors ${
                  isActive
                    ? 'bg-[#1B6EF3] text-white'
                    : 'text-[#8DA4BF] hover:text-white hover:bg-[#122a4e]'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <link.icon className="w-[18px] h-[18px]" />
              {link.label}
            </NavLink>
          ))}
        </nav>

        {/* Logout - mobile only */}
        <div className="px-3 pb-4 pt-3 border-t border-[#1a3050] mt-auto lg:hidden">
          <button onClick={() => { setSidebarOpen(false); logout(); navigate('/login'); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] text-red-400 font-medium rounded-lg hover:bg-[#122a4e] transition-colors">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile overlay - prevents interaction with content behind sidebar */}
      {sidebarOpen && <button type="button" className="fixed inset-0 bg-black/60 z-30 lg:hidden" tabIndex={0} aria-label="Close sidebar" onClick={() => setSidebarOpen(false)} onKeyDown={e => e.key === 'Enter' && setSidebarOpen(false)} onTouchMove={e => e.preventDefault()} />}

      {/* MAIN */}
      <main className={`flex-1 flex flex-col min-w-0 overflow-y-auto bg-[#F5F7FA] ${sidebarOpen ? 'lg:overflow-y-auto overflow-hidden' : ''}`}>
        {/* Header */}
        <header className="sticky top-0 z-10 bg-white border-b border-[#E8ECF0] px-3 sm:px-4 lg:px-6 h-[56px] sm:h-[60px] flex items-center gap-3 sm:gap-4 shrink-0">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <Menu className="w-5 h-5 text-[#64748b]" />
          </button>

          <div className="flex-1 min-w-0">
            <h1 className="text-[16px] font-semibold text-[#1e293b] leading-tight">{getRoleLabel()}</h1>
            <p className="text-[12px] text-[#64748b]">Welcome back, {userName}</p>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative hidden lg:block">
              <button className="flex items-center gap-2.5 pl-3 ml-1 border-l border-[#E8ECF0] cursor-pointer" aria-label="Toggle profile menu" aria-expanded={profileOpen} onClick={() => setProfileOpen(!profileOpen)}>
                <div className="w-8 h-8 rounded-full bg-[#1B6EF3] flex items-center justify-center text-white text-[11px] font-bold shrink-0">
                  {userName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="text-right">
                  <div className="text-[13px] font-semibold text-[#1e293b] leading-tight">{userName}</div>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-[#94a3b8] transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
              </button>

              {profileOpen && (
                <>
                  <button type="button" className="fixed inset-0 z-40" tabIndex={0} aria-label="Close profile menu" onClick={() => setProfileOpen(false)} onKeyDown={e => e.key === 'Enter' && setProfileOpen(false)} />
                  <div className="absolute top-full right-0 mt-2 w-[260px] bg-white border border-[#E2E8F0] rounded-xl shadow-lg z-50 overflow-hidden">
                    <div className="px-4 py-4 bg-[#f8fafc] border-b border-[#E2E8F0]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1B6EF3] flex items-center justify-center text-white text-[13px] font-bold shrink-0">
                          {userName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[14px] font-semibold text-[#1e293b] truncate">{userName}</div>
                          <div className="text-[12px] text-[#64748b] truncate">{user?.email || ''}</div>
                        </div>
                      </div>
                    </div>
                    <div className="px-4 py-3 space-y-2 border-b border-[#E2E8F0]">
                      <div className="flex items-center gap-2.5 text-[12px] text-[#475569]">
                        <svg className="w-4 h-4 text-[#94a3b8]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                        Role: <span className="font-medium text-[#1e293b]">{(() => {
                          const roleLabels = {
                            super_admin: 'Super Admin',
                            admin: 'Admin',
                            relationship_manager: 'Relationship Manager',
                            accounts_team: 'Accounts Team',
                            finance: 'Finance',
                            compliance: 'Compliance',
                            partner_admin: 'Partner Admin',
                            partner_user: 'Partner User'
                          };
                          const primaryRole = userRoles.find(r => roleLabels[r]) || userRoles[0];
                          return roleLabels[primaryRole] || primaryRole || 'User';
                        })()}</span>
                      </div>
                    </div>
                    <div className="px-3 py-2">
                      <button onClick={() => { setProfileOpen(false); logout(); navigate('/login'); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                        Logout
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-3 sm:p-4 lg:p-5 flex-1">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
